import React from 'react';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import {Provider} from 'react-redux';
import {createStore, combineReducers, compose} from 'redux';
import ConnectedIntlProvider from './connected-intl-provider.jsx';

import localesReducer, {initLocale, localesInitialState, selectLocale} from '../reducers/locales';
import sessionReducer, {sessionInitialState, setSession} from '../reducers/session';
import {setTheme} from '../reducers/theme';
import {setFloatingMode} from '../reducers/mode';

import {setPlayer, setFullScreen} from '../reducers/mode.js';

import locales from 'scratch-l10n';
import {detectLocale} from './detect-locale';
import {getStoredSession, getUser, setStoredSession, updateUser} from './supabase-auth';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

/*
 * Higher Order Component to provide redux state. If an `intl` prop is provided
 * it will override the internal `intl` redux state
 * @param {React.Component} WrappedComponent - component to provide state for
 * @param {boolean} localesOnly - only provide the locale state, not everything
 *                      required by the GUI. Used to exclude excess state when
                        only rendering modals, not the GUI.
 * @returns {React.Component} component with redux and intl state provided
 */
const AppStateHOC = function (WrappedComponent, localesOnly) {
    class AppStateWrapper extends React.Component {
        constructor (props) {
            super(props);
            this.preferenceSyncTimeout = null;
            this.preferenceSyncReady = false;
            this.lastPreferenceSignature = null;
            let initialState = {};
            let reducers = {};
            let enhancer;

            let initializedLocales = localesInitialState;
            const locale = detectLocale(Object.keys(locales));
            if (locale !== 'en') {
                initializedLocales = initLocale(initializedLocales, locale);
            }
            if (localesOnly) {
                // Used for instantiating minimal state for the unsupported
                // browser modal
                reducers = {
                    locales: localesReducer,
                    session: sessionReducer
                };
                initialState = {
                    locales: initializedLocales,
                    session: sessionInitialState
                };
                enhancer = composeEnhancers();
            } else {
                // You are right, this is gross. But it's necessary to avoid
                // importing unneeded code that will crash unsupported browsers.
                const guiRedux = require('../reducers/gui');
                const guiReducer = guiRedux.default;
                const {
                    guiInitialState,
                    guiMiddleware,
                    initFloatingMode,
                    initFullScreen,
                    initPlayer,
                    initTelemetryModal
                } = guiRedux;
                const {ScratchPaintReducer: BitBytePaintReducer} = require('scratch-paint');

                let initializedGui = guiInitialState;
                const storedSession = getStoredSession();
                const storedFloatingPreference = storedSession &&
                    storedSession.user &&
                    storedSession.user.user_metadata &&
                    typeof storedSession.user.user_metadata.isFloatingMode === 'boolean'
                    ? storedSession.user.user_metadata.isFloatingMode
                    : null;
                if (props.isFullScreen || props.isPlayerOnly) {
                    if (props.isFullScreen) {
                        initializedGui = initFullScreen(initializedGui);
                    }
                    if (props.isPlayerOnly) {
                        initializedGui = initPlayer(initializedGui);
                    }
                } else if (props.showTelemetryModal) {
                    initializedGui = initTelemetryModal(initializedGui);
                }

                const defaultFloatingEnabled = (
                    typeof window !== 'undefined' &&
                    window.innerWidth >= 1280 &&
                    !props.isFullScreen &&
                    !props.isPlayerOnly
                );
                if (storedFloatingPreference !== null) {
                    initializedGui = storedFloatingPreference
                        ? initFloatingMode(initializedGui)
                        : initializedGui;
                } else if (defaultFloatingEnabled) {
                    initializedGui = initFloatingMode(initializedGui);
                }
                reducers = {
                    locales: localesReducer,
                    bitByteGui: guiReducer,
                    scratchPaint: BitBytePaintReducer,
                    session: sessionReducer
                };
                initialState = {
                    locales: initializedLocales,
                    bitByteGui: initializedGui,
                    session: sessionInitialState
                };
                enhancer = composeEnhancers(guiMiddleware);
            }
            const reducer = combineReducers(reducers);
            this.store = createStore(
                reducer,
                initialState,
                enhancer
            );

            bindAll(this, [
                'handlePreferenceStoreChange',
                'syncPreferences'
            ]);
        }
        componentDidMount () {
            if (localesOnly) return;

            // Sync Supabase session and metadata
            const bootstrap = async () => {
                const storedSession = getStoredSession();
                if (!storedSession || !storedSession.access_token) return;

                try {
                    const user = await getUser(storedSession.access_token);
                    const nextSession = Object.assign({}, storedSession, {user});
                    setStoredSession(nextSession);
                    this.store.dispatch(setSession(nextSession));

                    const metadata = user?.user_metadata || {};
                    if (metadata.language) this.store.dispatch(selectLocale(metadata.language));
                    if (metadata.theme) this.store.dispatch(setTheme(metadata.theme));
                    if (typeof metadata.isFloatingMode !== 'undefined') {
                        this.store.dispatch(setFloatingMode(metadata.isFloatingMode));
                    }
                    this.lastPreferenceSignature = JSON.stringify({
                        language: metadata.language || initializedLocales.locale,
                        theme: metadata.theme || this.store.getState().bitByteGui.theme.theme,
                        isFloatingMode: typeof metadata.isFloatingMode === 'boolean'
                            ? metadata.isFloatingMode
                            : this.store.getState().bitByteGui.mode.isFloatingMode
                    });
                } catch (err) {
                    // Silently fail, might be expired token
                    setStoredSession(null);
                    this.store.dispatch(setSession(null));
                } finally {
                    this.preferenceSyncReady = true;
                    if (this.preferenceSyncTimeout === null) {
                        this.store.unsubscribePreferences = this.store.subscribe(this.handlePreferenceStoreChange);
                    }
                }
            };

            bootstrap();
        }
        componentWillUnmount () {
            if (this.preferenceSyncTimeout !== null) {
                clearTimeout(this.preferenceSyncTimeout);
                this.preferenceSyncTimeout = null;
            }
            if (this.store.unsubscribePreferences) {
                this.store.unsubscribePreferences();
                this.store.unsubscribePreferences = null;
            }
        }
        handlePreferenceStoreChange () {
            if (!this.preferenceSyncReady) return;
            if (this.preferenceSyncTimeout !== null) {
                clearTimeout(this.preferenceSyncTimeout);
            }
            this.preferenceSyncTimeout = setTimeout(this.syncPreferences, 300);
        }
        async syncPreferences () {
            this.preferenceSyncTimeout = null;
            const session = getStoredSession();
            if (!session || !session.access_token || !session.user) return;

            const state = this.store.getState();
            const nextPreferences = {
                language: state.locales.locale,
                theme: state.bitByteGui.theme.theme,
                isFloatingMode: state.bitByteGui.mode.isFloatingMode
            };
            const signature = JSON.stringify(nextPreferences);
            if (signature === this.lastPreferenceSignature) return;

            try {
                const updatedUserPayload = await updateUser(session.access_token, {
                    ...(session.user.user_metadata || {}),
                    ...nextPreferences
                });
                const updatedUser = updatedUserPayload?.user || updatedUserPayload;
                const nextSession = Object.assign({}, session, {user: updatedUser});
                setStoredSession(nextSession);
                this.store.dispatch(setSession(nextSession));
                this.lastPreferenceSignature = signature;
            } catch (err) {
                // Preference persistence is best-effort; editor should still work offline.
            }
        }
        componentDidUpdate (prevProps) {
            if (localesOnly) return;
            if (prevProps.isPlayerOnly !== this.props.isPlayerOnly) {
                this.store.dispatch(setPlayer(this.props.isPlayerOnly));
            }
            if (prevProps.isFullScreen !== this.props.isFullScreen) {
                this.store.dispatch(setFullScreen(this.props.isFullScreen));
            }
        }
        render () {
            const {
                isFullScreen, // eslint-disable-line no-unused-vars
                isPlayerOnly, // eslint-disable-line no-unused-vars
                showTelemetryModal, // eslint-disable-line no-unused-vars
                ...componentProps
            } = this.props;
            return (
                <Provider store={this.store}>
                    <ConnectedIntlProvider>
                        <WrappedComponent
                            {...componentProps}
                        />
                    </ConnectedIntlProvider>
                </Provider>
            );
        }
    }
    AppStateWrapper.propTypes = {
        isFullScreen: PropTypes.bool,
        isPlayerOnly: PropTypes.bool,
        isTelemetryEnabled: PropTypes.bool,
        showTelemetryModal: PropTypes.bool
    };
    return AppStateWrapper;
};

export default AppStateHOC;
